/**
 * Insurance Connector Machine
 *
 * XState v5 machine for handling insurance connection flow in the storefront.
 * Similar to the kiosk's MedriteCheckinMachine pattern but focused on insurance only.
 */

import type { ConsumerInsurance } from "@/app/directories/kiosk/server-actions/pc-actions";
import { and, assign, setup } from "xstate";
import {
  clearErrorAssigner,
  clearInsuranceAssigner,
  clearInsuranceFromStore,
  clearSecondaryInsuranceAssigner,
  resetFormAssigner,
} from "./actions/clear-insurance.action";
import {
  saveFormDataAssigner,
  saveReturnUserDataAssigner,
} from "./actions/save-form-data.action";
import {
  clearPendingPricesMode,
  enforceSelfPayMode,
  setPendingPricesMode,
  setStorefrontModeToCash,
} from "./actions/set-pending-prices-mode.action";
import {
  clearStoreInsurance,
  saveEligibilityFailedMinorConsentReferral,
  saveEligibilityFailedStateNotAvailable,
  saveEligibilityFailedUnderAge,
  saveEligibilityPassed,
  syncAllToStore,
  syncConsumerToStore,
  syncInsuranceToStore,
  syncMatchedIdentityToStore,
  syncStorefrontFlagsToStore,
  syncUnverifiedToStore,
  syncUserToStore,
} from "./actions/sync-global-store.action";
import {
  trackEligibilityAgeFailed,
  trackEligibilityAgePassed,
  trackEligibilityStateFailed,
  trackEligibilityStatePassed,
  trackInsuranceActive,
  trackInsuranceInactive,
  trackInsuranceOutOfNetwork,
  trackInsuranceUnverifiedFirst,
  trackInsuranceUnverifiedRetry,
  trackInsuranceVerificationError,
} from "./actions/track-insurance-events.action";
import { disambiguateConsumerActor } from "./actors/disambiguate-consumer.actor";
import {
  type MatchedConsumer,
  fetchConsumerActor,
} from "./actors/fetch-consumer.actor";
import { processInsuranceActorLogic } from "./actors/process-insurance.actor";
import { sendSMSActor } from "./actors/send-sms.actor";
import { verifySMSActor } from "./actors/verify-sms.actor";
import * as configGuards from "./guards/config.guard";
import * as validationGuards from "./guards/validation.guard";
import type {
  InsuranceConnectorContext,
  InsuranceConnectorEvent,
  InsuranceConnectorInput,
} from "./types";
import { mapConsumerInsurances } from "./utils";

type ActorDoneEvent<T = Record<string, unknown>> = { type: string; output: T };
type ActorErrorEvent<T = unknown> = { type: string; error: T };
type ErrorShape = InsuranceConnectorContext["error"];

/**
 * Create initial context from input
 */
function createInitialContext(
  input: InsuranceConnectorInput,
): InsuranceConnectorContext {
  return {
    orgId: input.orgId,
    orgName: input.orgName,
    storefrontOffboardConsumers: input.storefrontOffboardConsumers,
    containsEPSorWorkersComp:
      input.containsEPSorWorkersComp ?? !!input.workplaceInjuryRoutes,
    proceedWithUnverifiedInsurance: input.proceedWithUnverifiedInsurance,
    workplaceInjuryRoutes: input.workplaceInjuryRoutes,
    minimumAge: input.minimumAge,
    allowedStates: input.allowedStates,
    minorConsentReferralEnabled: input.minorConsentReferralEnabled,
    isOpen: false,
    uiMode: "closed",
    isProcessing: false,
    verificationAttempts: 0,
    passedEligibility: false,
  };
}

/**
 * Insurance Connector State Machine
 */
export const insuranceConnectorMachine = setup({
  types: {
    context: {} as InsuranceConnectorContext,
    events: {} as InsuranceConnectorEvent,
    input: {} as InsuranceConnectorInput,
  },
  guards: {
    // Config guards
    shouldAskState: configGuards.shouldAskState,
    shouldNotAskState: configGuards.shouldNotAskState,
    hasOrgId: configGuards.hasOrgId,
    hasOrgName: configGuards.hasOrgName,
    proceedWithUnverifiedInsuranceEnabled:
      configGuards.proceedWithUnverifiedInsuranceEnabled,
    hasPrimaryInsurance: configGuards.hasPrimaryInsurance,
    isDisconnectingSecondary: configGuards.isDisconnectingSecondary,
    isUnderMinimumAge: configGuards.isUnderMinimumAge,
    isStateNotAllowed: configGuards.isStateNotAllowed,
    isMinorConsentAgeFromEvent: configGuards.isMinorConsentAgeFromEvent,
    isMinorConsentAgeFromContext: configGuards.isMinorConsentAgeFromContext,
    hasBeenProcessed: ({ context }) =>
      !!context.lastVerificationResults || !!context.isRetryFlow,
    hasFailedEligibilityAge: ({ context }) =>
      context.eligibilityFailureCode === "under_aged",
    hasFailedEligibilityState: ({ context }) =>
      context.eligibilityFailureCode === "state_not_available",
    hasFailedEligibilityMinorConsent: ({ context }) =>
      context.eligibilityFailureCode === "minor_consent_referral",
    hasPassedEligibility: ({ context }) => context.passedEligibility === true,
    hasSeenIntro: ({ context }) => context.hasSeenIntro === true,
    // Validation guards
    hasValidName: validationGuards.hasValidName,
    hasValidBirthday: validationGuards.hasValidBirthday,
    hasValidPhoneNumber: validationGuards.hasValidPhoneNumber,
    hasValidState: validationGuards.hasValidState,
    hasValidInsurancePayer: validationGuards.hasValidInsurancePayer,
    hasValidMemberId: validationGuards.hasValidMemberId,
    hasValidInsuredState: validationGuards.hasValidInsuredState,
    hasValidPrimaryInsurance: validationGuards.hasValidPrimaryInsurance,
    hasValidSecondaryInsurance: validationGuards.hasValidSecondaryInsurance,
    wantsSecondaryInsurance: validationGuards.wantsSecondaryInsurance,
    hasAllRequiredData: validationGuards.hasAllRequiredData,
    isRetryFlow: ({ context }) => !!context.isRetryFlow,
    isNotRetryFlow: ({ context }) => !context.isRetryFlow,
    // Consumer match guards
    consumerMatchedWithInsurance: ({ event }) => {
      const { output } = event as ActorDoneEvent<{
        message?: string;
        consumer?: { insurances?: ConsumerInsurance[] };
      }>;
      const insurances = output?.consumer?.insurances;
      return (
        output?.message === "CONSUMER_MATCHED" &&
        Array.isArray(insurances) &&
        insurances.length > 0
      );
    },
    consumerMatched: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ message?: string }>;
      return output?.message === "CONSUMER_MATCHED";
    },
    multipleConsumersMatched: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ message?: string }>;
      return output?.message === "MULTIPLE_CONSUMERS_MATCHED";
    },
    // Zip is the only disambiguation field that renders a user-facing
    // prompt. Name disambiguation is resolved automatically by the machine
    // (see `multipleConsumersMatchedNameField` below) using the first/last
    // name we've already collected on the name step.
    multipleConsumersMatchedSupported: ({ event }) => {
      const { output } = event as ActorDoneEvent<{
        message?: string;
        disambiguationField?: string;
      }>;
      return (
        output?.message === "MULTIPLE_CONSUMERS_MATCHED" &&
        output.disambiguationField === "zip"
      );
    },
    // Name-based disambiguation: auto-retry with the firstName/lastName we
    // already have in context. No UI step.
    multipleConsumersMatchedNameField: ({ event, context }) => {
      const { output } = event as ActorDoneEvent<{
        message?: string;
        disambiguationField?: string;
      }>;
      return (
        output?.message === "MULTIPLE_CONSUMERS_MATCHED" &&
        output.disambiguationField === "name" &&
        !!context.firstName &&
        !!context.lastName
      );
    },
    noConsumerMatched: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ message?: string }>;
      return output?.message === "NO_CONSUMER_MATCHED";
    },
    // OTP guards
    otpInvalid: ({ event }) => {
      const { error } = event as ActorErrorEvent<{ code?: string }>;
      return (error as { code?: string })?.code === "OTP_INVALID";
    },
    // Insurance outcome guards
    isActiveOutcome: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ outcome?: string }>;
      const result = output?.outcome === "active";
      return result;
    },
    isUnverifiedOutcome: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ outcome?: string }>;
      const result = output?.outcome === "unverified";
      return result;
    },
    isInactiveOutcome: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ outcome?: string }>;
      const result = output?.outcome === "inactive";
      return result;
    },
    isOutOfNetworkOutcome: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ outcome?: string }>;
      const result = output?.outcome === "out_of_network";
      return result;
    },
    isUnverifiedRetryOutcome: ({ event, context }) => {
      const { output } = event as ActorDoneEvent<{ outcome?: string }>;
      return output?.outcome === "unverified" && !!context.isRetryFlow;
    },
    isUnverifiedFirstOutcome: ({ event, context }) => {
      const { output } = event as ActorDoneEvent<{ outcome?: string }>;
      return output?.outcome === "unverified" && !context.isRetryFlow;
    },
    // Disambiguation guards
    disambiguateMatchedWithInsurance: ({ event }) => {
      const { output } = event as ActorDoneEvent<{
        message?: string;
        consumer?: { insurances?: ConsumerInsurance[] };
      }>;
      const insurances = output?.consumer?.insurances;
      return (
        output?.message === "PATIENT_MATCHED" &&
        Array.isArray(insurances) &&
        insurances.length > 0
      );
    },
    disambiguateMatched: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ message?: string }>;
      return output?.message === "PATIENT_MATCHED";
    },
    disambiguateMultipleMatched: ({ event }) => {
      const { output } = event as ActorDoneEvent<{ message?: string }>;
      return output?.message === "MULTIPLE_PATIENTS_MATCHED";
    },
    // Zip-only for the re-disambiguation loop. Name auto-retries happen
    // at the machine level on the first `checkingReturnUser` pass, so by
    // the time we're inside `disambiguating` we should never be asked to
    // re-prompt for name.
    disambiguateMultipleMatchedSupported: ({ event }) => {
      const { output } = event as ActorDoneEvent<{
        message?: string;
        disambiguationField?: string;
      }>;
      return (
        output?.message === "MULTIPLE_PATIENTS_MATCHED" &&
        output.disambiguationField === "zip"
      );
    },
    underMaxDisambiguationAttempts: ({ context }) => {
      return (context.disambiguationAttempts ?? 0) < 1;
    },
    // Provider OON guard
    isProviderOonAllOON: ({ event }) => {
      return (
        event.type === "PROVIDER_OON_RESOLVED" &&
        (event as { type: "PROVIDER_OON_RESOLVED"; allOON: boolean }).allOON ===
          true
      );
    },
  },
  actions: {
    saveFormData: assign(saveFormDataAssigner),
    saveReturnUserData: assign(saveReturnUserDataAssigner),
    clearInsurance: assign(clearInsuranceAssigner),
    clearInsuranceFromStore,
    clearSecondaryInsurance: assign(clearSecondaryInsuranceAssigner),
    clearError: assign(clearErrorAssigner),
    resetForm: assign(resetFormAssigner),
    syncInsuranceToStore,
    syncConsumerToStore,
    syncUserToStore,
    syncAllToStore,
    syncUnverifiedToStore,
    syncMatchedIdentityToStore,
    syncStorefrontFlagsToStore,
    clearStoreInsurance,
    clearPendingPricesMode,
    enforceSelfPayMode,
    setPendingPricesMode,
    setStorefrontModeToCash,
    saveEligibilityFailedUnderAge,
    saveEligibilityFailedStateNotAvailable,
    saveEligibilityFailedMinorConsentReferral,
    saveEligibilityPassed,
    // Hydrate context from a successful match (fetchConsumer OR
    // disambiguateConsumer — both return the unified `MatchedConsumer`
    // shape under `output.consumer`). Sets consumer identity, user id,
    // email, the full consumerData block, and maps any existing
    // insurances into primary/secondary.
    assignMatchedConsumer: assign(({ event }) => {
      const { output } = event as ActorDoneEvent<{
        consumer?: MatchedConsumer;
        userPhone?: string;
      }>;
      const consumer = output.consumer;
      const mapped = mapConsumerInsurances(consumer?.insurances || []);
      return {
        isReturnUser: !!consumer,
        consumerId: consumer?.id,
        userId: consumer?.userId,
        userEmail: consumer?.email,
        ...(output.userPhone ? { userPhone: output.userPhone } : {}),
        consumerData: consumer
          ? {
              id: consumer.id,
              userId: consumer.userId,
              firstname: consumer.firstname,
              lastname: consumer.lastname,
              dob: consumer.dob,
              email: consumer.email,
            }
          : undefined,
        ...mapped,
      };
    }),
  },
  actors: {
    processInsurance: processInsuranceActorLogic,
    fetchConsumer: fetchConsumerActor,
    disambiguateConsumer: disambiguateConsumerActor,
    sendSMS: sendSMSActor,
    verifySMS: verifySMSActor,
  },
}).createMachine({
  id: "insuranceConnector",
  initial: "closed",
  context: ({ input }) => createInitialContext(input),

  on: {
    SET_CONTENT_OVERRIDES: {
      actions: assign(({ event }) => {
        const { data } = event as {
          type: "SET_CONTENT_OVERRIDES";
          data: import("./types").ContentOverrides;
        };
        return { contentOverrides: data };
      }),
    },
  },

  states: {
    // ===== CLOSED STATE =====
    closed: {
      entry: [
        assign({
          uiMode: "closed",
          isOpen: false,
        }),
      ],
      on: {
        OPEN: [
          {
            guard: "hasPrimaryInsurance",
            target: "minimized.insuranceMenu",
          },
          {
            guard: "hasFailedEligibilityAge",
            target: "full.eligibilityAgeRejection",
          },
          {
            guard: "hasFailedEligibilityState",
            target: "full.eligibilityStateRejection",
          },
          // A referred 15, 16 or 17 year old who dismissed the consent screen
          // (or reloaded the page) reopens straight onto it, rather than
          // being sent back through the eligibility questions.
          {
            guard: "hasFailedEligibilityMinorConsent",
            target: "full.eligibilityMinorConsentReferral",
          },
          {
            guard: and([
              "shouldAskState",
              "hasPassedEligibility",
              "hasSeenIntro",
            ]),
            target: "full.nameInput",
          },
          {
            guard: and(["shouldAskState", "hasPassedEligibility"]),
            target: "full.introScreen",
          },
          {
            guard: "shouldAskState",
            target: "full.eligibilityWelcome",
          },
          {
            guard: "hasSeenIntro",
            target: "full.nameInput",
          },
          {
            target: "full",
          },
        ],
        YES_CONNECT: {
          target: "full.nameInput",
        },
        TRY_ANOTHER: {
          target: "full.insurancePayer",
        },
        HYDRATE_FROM_STORE: {
          target: "connected",
          actions: assign(({ event }) => {
            const { data } = event as {
              type: "HYDRATE_FROM_STORE";
              data: {
                firstName: string;
                lastName: string;
                birthday: string;
                phoneNumber: string;
                consumerId?: string;
                userId?: string;
                primaryInsurance: {
                  payer: string;
                  memberId: string;
                  insuredState: string;
                  planName?: string;
                };
                lastVerificationResults?: InsuranceConnectorContext["lastVerificationResults"];
              };
            };
            return {
              firstName: data.firstName,
              lastName: data.lastName,
              birthday: data.birthday,
              phoneNumber: data.phoneNumber,
              consumerId: data.consumerId,
              userId: data.userId,
              primaryInsurance: {
                payer: data.primaryInsurance.payer,
                memberId: data.primaryInsurance.memberId,
                insuredState: data.primaryInsurance.insuredState,
                planName: data.primaryInsurance.planName,
              },
              lastVerificationResults: data.lastVerificationResults,
            };
          }),
        },
        // Rehydrate eligibility-failure context from the persisted global
        // store without leaving `closed`. This lets `OPEN` later route
        // directly to the correct rejection screen via the
        // `hasFailedEligibility*` guards after a page reload.
        HYDRATE_ELIGIBILITY_FROM_STORE: {
          actions: assign(({ event }) => {
            const { data } = event as {
              type: "HYDRATE_ELIGIBILITY_FROM_STORE";
              data: {
                eligibilityFailureCode: "under_aged" | "state_not_available";
                consumerState?: string;
                minimumAge?: number;
              };
            };
            return {
              eligibilityFailureCode: data.eligibilityFailureCode,
              consumerState: data.consumerState,
              minimumAge: data.minimumAge,
            };
          }),
        },
        // Rehydrate passed eligibility context from the persisted global
        // store. This lets `OPEN` skip the eligibility questions and go
        // directly to the intro screen via `hasPassedEligibility` guard.
        HYDRATE_ELIGIBILITY_PASSED_FROM_STORE: {
          actions: assign(({ context, event }) => {
            const { data } = event as {
              type: "HYDRATE_ELIGIBILITY_PASSED_FROM_STORE";
              data: {
                birthday?: string;
                consumerState?: string;
              };
            };
            // Only overwrite birthday/state when the persisted record
            // actually carries a value — older sessions (or a switched
            // org) may have a "passed" eligibility result with no
            // birthday, and clobbering context with `undefined` here
            // breaks downstream actors that expect MM/DD/YYYY.
            return {
              passedEligibility: true,
              ...(data.birthday ? { birthday: data.birthday } : {}),
              ...(data.consumerState
                ? { consumerState: data.consumerState }
                : {}),
            };
          }),
        },
      },
    },

    // ===== FULL UI MODE (FORM INPUT STATES) =====
    full: {
      initial: "introScreen",
      entry: assign({
        uiMode: "full",
        isOpen: true,
      }),
      on: {
        CLOSE: [
          {
            guard: and(["hasPrimaryInsurance", "hasBeenProcessed"]),
            target: "#insuranceConnector.connected",
          },
          {
            target: "#insuranceConnector.closed",
            actions: ["clearInsurance", "clearStoreInsurance"],
          },
        ],
      },
      states: {
        // ===== ELIGIBILITY FLOW =====
        eligibilityWelcome: {
          meta: {
            componentName: "EligibilityWelcome",
          },
          on: {
            NEXT: {
              target: "eligibilityBirthdayInput",
            },
          },
        },

        eligibilityBirthdayInput: {
          meta: {
            componentName: "EligibilityBirthdayInput",
          },
          on: {
            SUBMIT: [
              // A 15, 16 or 17 year old at an org with the consent referral
              // enabled is NOT rejected here. They are still asked for their
              // state, and the referral is shown after that passes, so the
              // state restriction keeps precedence over the referral.
              {
                guard: "isMinorConsentAgeFromEvent",
                target: "eligibilityStateQuestion",
                actions: ["saveFormData"],
              },
              {
                guard: "isUnderMinimumAge",
                target: "eligibilityAgeRejection",
                actions: [
                  "saveFormData",
                  "saveEligibilityFailedUnderAge",
                  assign({
                    eligibilityFailureCode: "under_aged" as const,
                  }),
                  trackEligibilityAgeFailed,
                ],
              },
              {
                target: "eligibilityStateQuestion",
                actions: ["saveFormData", trackEligibilityAgePassed],
              },
            ],
            BACK: {
              target: "eligibilityWelcome",
            },
          },
        },

        eligibilityAgeRejection: {
          meta: {
            componentName: "EligibilityAgeRejection",
          },
          on: {
            DISMISS: {
              target: "#insuranceConnector.closed",
            },
          },
        },

        eligibilityStateQuestion: {
          meta: {
            componentName: "EligibilityStateQuestion",
          },
          on: {
            SUBMIT: [
              {
                guard: "isStateNotAllowed",
                target: "eligibilityStateRejection",
                actions: [
                  "saveFormData",
                  "saveEligibilityFailedStateNotAvailable",
                  assign({
                    eligibilityFailureCode: "state_not_available" as const,
                  }),
                  trackEligibilityStateFailed,
                ],
              },
              // Age and state have both been collected and the state is
              // served, so a 15, 16 or 17 year old now gets the parent/guardian
              // consent referral instead of continuing into the booking flow.
              // The outcome is persisted like the two rejections, so
              // reopening the connector (or reloading) returns here.
              {
                guard: "isMinorConsentAgeFromContext",
                target: "eligibilityMinorConsentReferral",
                actions: [
                  "saveFormData",
                  "saveEligibilityFailedMinorConsentReferral",
                  assign({
                    eligibilityFailureCode: "minor_consent_referral" as const,
                  }),
                ],
              },
              {
                target: "introScreen",
                actions: [
                  "saveFormData",
                  "saveEligibilityPassed",
                  assign({ passedEligibility: true }),
                  trackEligibilityStatePassed,
                ],
              },
            ],
            BACK: {
              target: "eligibilityBirthdayInput",
            },
          },
        },

        eligibilityMinorConsentReferral: {
          meta: {
            componentName: "EligibilityMinorConsentReferral",
          },
          on: {
            DISMISS: {
              target: "#insuranceConnector.closed",
            },
          },
        },

        eligibilityStateRejection: {
          meta: {
            componentName: "EligibilityStateRejection",
          },
          on: {
            DISMISS: {
              target: "#insuranceConnector.closed",
            },
          },
        },

        // ===== INTRO SCREEN =====
        introScreen: {
          meta: {
            componentName: "IntroScreen",
          },
          exit: assign({ hasSeenIntro: true }),
          on: {
            YES_CONNECT: {
              target: "nameInput",
            },
            NOT_NOW: {
              target: "#insuranceConnector.closed",
            },
            EMPLOYER_SENT: {
              target: "workplaceInjuryQuestion",
            },
          },
        },

        // ===== WORKPLACE INJURY QUESTION =====
        workplaceInjuryQuestion: {
          meta: {
            componentName: "WorkplaceInjuryQuestion",
          },
          on: {
            BACK: {
              target: "introScreen",
            },
          },
        },

        // ===== NAME INPUT =====
        // When `shouldAskState` is true, the eligibility flow has already
        // collected the consumer's birthday and state, so we skip the
        // `birthdayInput` step and go straight to `phoneNumberInput`.
        nameInput: {
          meta: {
            componentName: "NameInput",
          },
          on: {
            SUBMIT: [
              {
                guard: "shouldAskState",
                target: "phoneNumberInput",
                actions: "saveFormData",
              },
              {
                target: "birthdayInput",
                actions: "saveFormData",
              },
            ],
            BACK: [
              {
                guard: "hasSeenIntro",
                target: "#insuranceConnector.closed",
              },
              {
                target: "introScreen",
              },
            ],
          },
        },

        // ===== BIRTHDAY INPUT =====
        birthdayInput: {
          meta: {
            componentName: "BirthdayInput",
          },
          on: {
            SUBMIT: {
              target: "phoneNumberInput",
              actions: "saveFormData",
            },
            BACK: {
              target: "nameInput",
            },
          },
        },

        // ===== PHONE NUMBER INPUT =====
        phoneNumberInput: {
          meta: {
            componentName: "PhoneNumberInput",
          },
          on: {
            SUBMIT: {
              guard: "hasValidPhoneNumber",
              target: "checkingReturnUser",
              actions: "saveFormData",
            },
            BACK: [
              {
                guard: "shouldAskState",
                target: "nameInput",
              },
              {
                target: "birthdayInput",
              },
            ],
          },
        },

        // ===== CHECKING RETURN USER =====
        checkingReturnUser: {
          meta: {
            componentName: "LoadingScreen",
          },
          invoke: {
            src: "fetchConsumer",
            input: ({ context }) => ({
              firstName: context.firstName || "",
              lastName: context.lastName || "",
              birthday: context.birthday || "",
              phoneNumber: context.phoneNumber || "",
              orgId: context.orgId,
            }),
            onDone: [
              {
                // Returning consumer with existing insurance — hydrate
                // context + identity store, then verify via OTP. Insurance
                // is kept in context only; it is NOT synced to the global
                // store until OTP succeeds (see verifyingSMS.onDone).
                guard: "consumerMatchedWithInsurance",
                target: "sendingSMS",
                actions: [
                  "assignMatchedConsumer",
                  "syncMatchedIdentityToStore",
                ],
              },
              {
                // Single consumer matched (no insurance) — hydrate identity
                // so review/checkout surfaces have name/email even if OTP
                // later bails.
                guard: "consumerMatched",
                target: "sendingSMS",
                actions: [
                  "assignMatchedConsumer",
                  "syncMatchedIdentityToStore",
                ],
              },
              {
                // Multiple consumers, disambiguation field is "name" AND
                // we already collected firstName/lastName on the name step:
                // auto-disambiguate without showing a UI prompt.
                guard: "multipleConsumersMatchedNameField",
                target: "disambiguating",
                actions: assign(({ event, context }) => {
                  const { output } = event as ActorDoneEvent<{
                    correlationId?: string;
                    disambiguationField?: string;
                  }>;
                  return {
                    correlationId: output.correlationId,
                    disambiguationField: "name" as const,
                    disambiguationAnswer: {
                      firstName: context.firstName || "",
                      lastName: context.lastName || "",
                    },
                    disambiguationAttempts: 0,
                    userId: undefined,
                    consumerId: undefined,
                  };
                }),
              },
              {
                // Multiple consumers with `zip` disambiguation — prompt the
                // user via `DisambiguationQuestion`.
                guard: "multipleConsumersMatchedSupported",
                target: "disambiguationQuestion",
                actions: assign(({ event }) => {
                  const { output } = event as ActorDoneEvent<{
                    correlationId?: string;
                    disambiguationField?: string;
                  }>;
                  return {
                    correlationId: output.correlationId,
                    disambiguationField:
                      output.disambiguationField as InsuranceConnectorContext["disambiguationField"],
                    disambiguationAttempts: 0,
                    userId: undefined,
                    consumerId: undefined,
                  };
                }),
              },
              {
                // Multiple matches on an unsupported field (or name field
                // without firstName/lastName in context) — we don't prompt
                // for address/email/city/state. Treat as a new consumer
                // and proceed to insurance capture.
                guard: "multipleConsumersMatched",
                target: "insurancePayer",
                actions: assign({
                  userId: undefined,
                  consumerId: undefined,
                }),
              },
              {
                // No consumer matched - proceed as new user
                guard: "noConsumerMatched",
                target: "insurancePayer",
              },
            ],
            onError: {
              target: "insurancePayer",
            },
          },
        },

        // ===== OTP VERIFICATION FLOW =====
        sendingSMS: {
          meta: {
            componentName: "LoadingScreen",
          },
          invoke: {
            src: "sendSMS",
            input: ({ context }) => ({
              userId: context.userId || "",
              phoneNumber: context.phoneNumber || "",
              orgName: context.orgName || "",
            }),
            onDone: {
              target: "otpInput",
              actions: assign(({ event }) => ({
                otpTime: event.output.otpTime,
                userId: event.output.userId,
              })),
            },
            onError: {
              // If SMS fails, proceed without OTP (treat as new user)
              target: "insurancePayer",
              actions: assign({
                error: ({ event }) => event.error as ErrorShape,
              }),
            },
          },
        },

        otpInput: {
          meta: {
            componentName: "OTPInput",
          },
          on: {
            SUBMIT: {
              target: "verifyingSMS",
              actions: "saveFormData",
            },
            RESEND: {
              target: "sendingSMS",
              actions: assign({ error: undefined, otp: undefined }),
            },
            BACK: {
              target: "phoneNumberInput",
              actions: assign({ otp: undefined, error: undefined }),
            },
          },
        },

        verifyingSMS: {
          meta: {
            componentName: "LoadingScreen",
          },
          invoke: {
            src: "verifySMS",
            input: ({ context }) => ({
              userId: context.userId || "",
              otp: context.otp || "",
              otpTime: context.otpTime || "",
            }),
            onDone: [
              {
                // Return user with existing insurance — OTP succeeded, so
                // now sync the matched insurance to the global store (it
                // was withheld pre-OTP). `processInsurance` will run next
                // and may upgrade the store via `syncAllToStore` on an
                // active outcome.
                guard: "hasPrimaryInsurance",
                target: "#insuranceConnector.minimized",
                actions: [assign({ verified: true }), "syncInsuranceToStore"],
              },
              {
                target: "insurancePayer",
                actions: assign({ verified: true }),
              },
            ],
            onError: [
              {
                // Invalid OTP - show error and return to input
                guard: "otpInvalid",
                target: "otpInput",
                actions: assign({
                  error: ({ event }) => event.error as ErrorShape,
                  otp: undefined,
                }),
              },
              {
                // Other errors - proceed without OTP
                target: "insurancePayer",
                actions: assign({
                  error: ({ event }) => event.error as ErrorShape,
                }),
              },
            ],
          },
        },

        // ===== DISAMBIGUATION QUESTION =====
        disambiguationQuestion: {
          meta: {
            componentName: "DisambiguationQuestion",
          },
          on: {
            SUBMIT: {
              target: "disambiguating",
              actions: assign(({ event }) => ({
                disambiguationAnswer: (
                  event as { type: "SUBMIT"; data: Record<string, unknown> }
                ).data as Record<string, string>,
              })),
            },
            BACK: {
              target: "phoneNumberInput",
            },
          },
        },

        // ===== DISAMBIGUATING (calls disambiguate endpoint) =====
        disambiguating: {
          meta: {
            componentName: "LoadingScreen",
          },
          invoke: {
            src: "disambiguateConsumer",
            input: ({ context }) => ({
              phoneNumber: context.phoneNumber || "",
              birthday: context.birthday || "",
              orgId: context.orgId,
              correlationId: context.correlationId || "",
              disambiguationAnswer: context.disambiguationAnswer || {},
            }),
            onDone: [
              {
                // Disambiguated consumer with existing insurance — hydrate
                // context + identity store, then verify via OTP. Insurance
                // stays in context only until OTP succeeds.
                guard: "disambiguateMatchedWithInsurance",
                target: "sendingSMS",
                actions: [
                  "assignMatchedConsumer",
                  "syncMatchedIdentityToStore",
                ],
              },
              {
                guard: "disambiguateMatched",
                target: "sendingSMS",
                actions: [
                  "assignMatchedConsumer",
                  "syncMatchedIdentityToStore",
                ],
              },
              {
                // Still multiple matches and the next disambiguation field
                // is `zip` — re-prompt if under max attempts.
                guard: and([
                  "disambiguateMultipleMatchedSupported",
                  "underMaxDisambiguationAttempts",
                ]),
                target: "disambiguationQuestion",
                actions: assign(({ event, context }) => {
                  const { output } = event as ActorDoneEvent<{
                    disambiguationField?: string;
                    correlationId?: string;
                  }>;
                  return {
                    disambiguationAttempts:
                      (context.disambiguationAttempts ?? 0) + 1,
                    disambiguationField:
                      output.disambiguationField as InsuranceConnectorContext["disambiguationField"],
                    correlationId: output.correlationId,
                  };
                }),
              },
              {
                // Still multiple matches but on an unsupported field (or max
                // attempts reached) — treat as a new consumer.
                target: "insurancePayer",
              },
            ],
            onError: {
              target: "insurancePayer",
            },
          },
        },

        // ===== PRIMARY INSURANCE PAYER =====
        insurancePayer: {
          meta: {
            componentName: "InsurancePayer",
          },
          on: {
            SUBMIT: {
              target: "memberIdInput",
              actions: "saveFormData",
            },
            BACK: {
              target: "phoneNumberInput",
            },
          },
        },

        // ===== MEMBER ID INPUT =====
        memberIdInput: {
          meta: {
            componentName: "MemberIdInput",
          },
          on: {
            SUBMIT: {
              target: "insuredStateInput",
              actions: "saveFormData",
            },
            BACK: {
              target: "insurancePayer",
            },
          },
        },

        // ===== INSURED STATE INPUT =====
        insuredStateInput: {
          meta: {
            componentName: "InsuredStateInput",
          },
          on: {
            SUBMIT: {
              target: "secondaryQuestion",
              actions: "saveFormData",
            },
            BACK: {
              target: "memberIdInput",
            },
          },
        },

        // ===== SECONDARY INSURANCE QUESTION =====
        secondaryQuestion: {
          meta: {
            componentName: "SecondaryQuestion",
          },
          on: {
            YES_SECONDARY: {
              target: "secondaryPayerInput",
            },
            NO_SECONDARY: {
              target: "#insuranceConnector.minimized",
            },
            BACK: {
              target: "insuredStateInput",
            },
          },
        },

        // ===== SECONDARY INSURANCE PAYER (reuses InsurancePayer component) =====
        secondaryPayerInput: {
          meta: {
            componentName: "InsurancePayer",
            insurancePriority: "secondary",
          },
          on: {
            SUBMIT: {
              target: "secondaryMemberIdInput",
              actions: "saveFormData",
            },
            BACK: {
              target: "secondaryQuestion",
            },
          },
        },

        // ===== SECONDARY MEMBER ID (reuses MemberIdInput component) =====
        secondaryMemberIdInput: {
          meta: {
            componentName: "MemberIdInput",
            insurancePriority: "secondary",
          },
          on: {
            SUBMIT: {
              target: "secondaryInsuredStateInput",
              actions: "saveFormData",
            },
            BACK: {
              target: "secondaryPayerInput",
            },
          },
        },

        // ===== SECONDARY INSURED STATE (reuses InsuredStateInput component) =====
        secondaryInsuredStateInput: {
          meta: {
            componentName: "InsuredStateInput",
            insurancePriority: "secondary",
          },
          on: {
            SUBMIT: {
              target: "#insuranceConnector.minimized",
              actions: "saveFormData",
            },
            BACK: {
              target: "secondaryMemberIdInput",
            },
          },
        },

        // ===== UNVERIFIED RETRY FLOW =====
        unverifiedRetryPayer: {
          meta: {
            componentName: "InsurancePayer",
            variant: "retry",
          },
          on: {
            SUBMIT: {
              target: "unverifiedRetryMemberId",
              actions: assign({
                primaryInsurance: ({ context, event }) => ({
                  payer:
                    (event.data?.payer as string) ||
                    context.primaryInsurance?.payer ||
                    "",
                  payerId:
                    (event.data?.payerId as string) ||
                    context.primaryInsurance?.payerId ||
                    "",
                  memberId: context.primaryInsurance?.memberId || "",
                  insuredState: context.primaryInsurance?.insuredState || "",
                  insuredFirstName: context.primaryInsurance?.insuredFirstName,
                  insuredLastName: context.primaryInsurance?.insuredLastName,
                }),
              }),
            },
          },
        },

        unverifiedRetryMemberId: {
          meta: {
            componentName: "MemberIdInput",
            variant: "retry",
          },
          on: {
            SUBMIT: {
              target: "unverifiedRetryInsuredState",
              actions: assign({
                primaryInsurance: ({ context, event }) => ({
                  payer: context.primaryInsurance?.payer || "",
                  payerId: context.primaryInsurance?.payerId,
                  memberId:
                    (event.data?.memberId as string) ||
                    context.primaryInsurance?.memberId ||
                    "",
                  insuredState: context.primaryInsurance?.insuredState || "",
                  insuredFirstName: context.primaryInsurance?.insuredFirstName,
                  insuredLastName: context.primaryInsurance?.insuredLastName,
                }),
              }),
            },
            BACK: {
              target: "unverifiedRetryPayer",
            },
          },
        },

        unverifiedRetryInsuredState: {
          meta: {
            componentName: "InsuredStateInput",
            variant: "retry",
          },
          on: {
            SUBMIT: {
              target: "unverifiedRetryName",
              actions: assign({
                primaryInsurance: ({ context, event }) => ({
                  payer: context.primaryInsurance?.payer || "",
                  payerId: context.primaryInsurance?.payerId,
                  memberId: context.primaryInsurance?.memberId || "",
                  insuredState:
                    (event.data?.insuredState as string) ||
                    context.primaryInsurance?.insuredState ||
                    "",
                  insuredFirstName: context.primaryInsurance?.insuredFirstName,
                  insuredLastName: context.primaryInsurance?.insuredLastName,
                }),
              }),
            },
            BACK: {
              target: "unverifiedRetryMemberId",
            },
          },
        },

        unverifiedRetryName: {
          meta: {
            componentName: "NameInput",
            variant: "retry",
          },
          on: {
            SUBMIT: {
              target: "#insuranceConnector.minimized",
              actions: assign({
                firstName: ({ event, context }) =>
                  (event.data?.firstName as string) || context.firstName || "",
                lastName: ({ event, context }) =>
                  (event.data?.lastName as string) || context.lastName || "",
                primaryInsurance: ({ context, event }) => ({
                  payer: context.primaryInsurance?.payer || "",
                  payerId: context.primaryInsurance?.payerId,
                  memberId: context.primaryInsurance?.memberId || "",
                  insuredState: context.primaryInsurance?.insuredState || "",
                  insuredFirstName:
                    (event.data?.firstName as string) ||
                    context.primaryInsurance?.insuredFirstName ||
                    "",
                  insuredLastName:
                    (event.data?.lastName as string) ||
                    context.primaryInsurance?.insuredLastName ||
                    "",
                }),
                uiMode: "minimized",
              }),
            },
            BACK: {
              target: "unverifiedRetryInsuredState",
            },
          },
        },
      },
    },

    // ===== MINIMIZED UI MODE (VERIFICATION & RESULTS) =====
    minimized: {
      initial: "loadingInsurance",
      entry: [
        assign({
          uiMode: "minimized",
          isOpen: true,
        }),
      ],
      on: {
        CLOSE: [
          {
            guard: "hasPrimaryInsurance",
            target: "#insuranceConnector.connected",
          },
          {
            target: "#insuranceConnector.closed",
          },
        ],
      },
      states: {
        // ===== LOADING INSURANCE =====
        loadingInsurance: {
          meta: {
            componentName: "LoadingScreen",
          },
          entry: [assign({ isProcessing: true })],
          exit: assign({ isProcessing: false }),
          invoke: {
            src: "processInsurance",
            input: ({ context }) => ({
              consumer: {
                id: context.consumerId || "",
                fname:
                  context.primaryInsurance?.insuredFirstName ||
                  context.firstName ||
                  "",
                lname:
                  context.primaryInsurance?.insuredLastName ||
                  context.lastName ||
                  "",
                dob: (context.birthday || "")
                  .replace(/\s/g, "")
                  .replace(/\//g, "-"),
                sex: undefined,
              },
              insurances: [
                context.primaryInsurance && {
                  payer: context.primaryInsurance.payer,
                  memberId: context.primaryInsurance.memberId,
                  state: context.primaryInsurance.insuredState,
                },
                context.secondaryInsurance && {
                  payer: context.secondaryInsurance.payer,
                  memberId: context.secondaryInsurance.memberId,
                  state: context.secondaryInsurance.insuredState,
                },
              ].filter(Boolean) as Array<{
                payer: string;
                memberId: string;
                state: string;
              }>,
              orgId: context.orgId,
            }),
            onDone: [
              {
                guard: "isActiveOutcome",
                target: "#insuranceConnector.minimized.successConfirmation",
                actions: [
                  assign(({ event, context }) => {
                    const statuses = event.output.insuranceStates;
                    const primaryStatus = statuses[0];
                    const secondaryStatus = statuses[1];
                    return {
                      error: undefined,
                      lastVerificationResults: statuses,
                      primaryInsurance: context.primaryInsurance
                        ? {
                            ...context.primaryInsurance,
                            planName: primaryStatus?.planName,
                            status: primaryStatus?.status,
                          }
                        : undefined,
                      secondaryInsurance: context.secondaryInsurance
                        ? {
                            ...context.secondaryInsurance,
                            planName: secondaryStatus?.planName,
                            status: secondaryStatus?.status,
                          }
                        : undefined,
                    };
                  }),
                  "syncAllToStore",
                  trackInsuranceActive,
                ],
              },
              // ----- Non-active outcomes -----
              //
              // For every non-active outcome below (inactive, out_of_network,
              // unverified-first, unverified-retry, retry-flow fallback,
              // default fallback) we ALWAYS:
              //   1. Persist the insurance / consumer / user to the global
              //      store via `syncUnverifiedToStore`. This keeps the card
              //      visible in `InsuranceMenu`, preserves the consumer's
              //      name in the "Hi, {name}" header, and lets downstream
              //      surfaces (review, checkout, appointment creation) see
              //      who the patient is even when verification didn't
              //      succeed. Without this sync, new users (no
              //      `syncMatchedConsumerToStore` ran) reach the connected
              //      menu with `consumer.fname === ""` — which is what
              //      caused the "name disappears" bug.
              //   2. Force the storefront into self-pay mode via
              //      `enforceSelfPayMode` (sets `storefrontPaymentMode` to
              //      "CASH" and `isPendingPricesMode` to false). This is
              //      what blocks the price toggle from flipping to INSURED
              //      and rendering $0 / $NaN prices that have no INSURED
              //      price data backing them.
              // The unverified-second `PROCEED_WITH_UNVERIFIED` branch is
              // the only path that intentionally flips back to INSURED +
              // pending-prices mode; that override happens later in the
              // result state itself.
              {
                guard: "isInactiveOutcome",
                target: "#insuranceConnector.minimized.inactiveResult",
                actions: [
                  assign(({ event, context }) => ({
                    lastVerificationResults: event.output.insuranceStates,
                    storefrontPaymentMode: "CASH" as const,
                    isPendingPricesMode: false,
                    primaryInsurance: context.primaryInsurance
                      ? {
                          ...context.primaryInsurance,
                          planName: event.output.insuranceStates[0]?.planName,
                          status: event.output.insuranceStates[0]?.status,
                        }
                      : undefined,
                  })),
                  "syncUnverifiedToStore",
                  enforceSelfPayMode,
                  trackInsuranceInactive,
                ],
              },
              {
                guard: "isOutOfNetworkOutcome",
                target: "#insuranceConnector.minimized.outOfNetworkResult",
                actions: [
                  assign(({ event, context }) => ({
                    lastVerificationResults: event.output.insuranceStates,
                    storefrontPaymentMode: "CASH" as const,
                    isPendingPricesMode: false,
                    primaryInsurance: context.primaryInsurance
                      ? {
                          ...context.primaryInsurance,
                          planName: event.output.insuranceStates[0]?.planName,
                          status: event.output.insuranceStates[0]?.status,
                        }
                      : undefined,
                  })),
                  "syncUnverifiedToStore",
                  enforceSelfPayMode,
                  trackInsuranceOutOfNetwork,
                ],
              },
              {
                guard: "isUnverifiedRetryOutcome",
                target: "#insuranceConnector.minimized.unverifiedSecondResult",
                actions: [
                  assign(({ event, context }) => ({
                    lastVerificationResults: event.output.insuranceStates,
                    verificationAttempts: context.verificationAttempts + 1,
                    storefrontPaymentMode: "CASH" as const,
                    isPendingPricesMode: false,
                    primaryInsurance: context.primaryInsurance
                      ? {
                          ...context.primaryInsurance,
                          planName: event.output.insuranceStates[0]?.planName,
                          status: event.output.insuranceStates[0]?.status,
                        }
                      : undefined,
                  })),
                  "syncUnverifiedToStore",
                  enforceSelfPayMode,
                  trackInsuranceUnverifiedRetry,
                ],
              },
              {
                guard: "isUnverifiedFirstOutcome",
                target: "#insuranceConnector.minimized.unverifiedResult",
                actions: [
                  assign(({ event, context }) => ({
                    lastVerificationResults: event.output.insuranceStates,
                    verificationAttempts: context.verificationAttempts + 1,
                    storefrontPaymentMode: "CASH" as const,
                    isPendingPricesMode: false,
                    primaryInsurance: context.primaryInsurance
                      ? {
                          ...context.primaryInsurance,
                          planName: event.output.insuranceStates[0]?.planName,
                          status: event.output.insuranceStates[0]?.status,
                        }
                      : undefined,
                  })),
                  "syncUnverifiedToStore",
                  enforceSelfPayMode,
                  trackInsuranceUnverifiedFirst,
                ],
              },
              {
                guard: "isRetryFlow",
                target: "#insuranceConnector.minimized.unverifiedSecondResult",
                actions: [
                  assign(({ event, context }) => ({
                    lastVerificationResults: event.output.insuranceStates,
                    storefrontPaymentMode: "CASH" as const,
                    isPendingPricesMode: false,
                    primaryInsurance: context.primaryInsurance
                      ? {
                          ...context.primaryInsurance,
                          planName: event.output.insuranceStates[0]?.planName,
                          status: event.output.insuranceStates[0]?.status,
                        }
                      : undefined,
                  })),
                  "syncUnverifiedToStore",
                  enforceSelfPayMode,
                  trackInsuranceUnverifiedRetry,
                ],
              },
              {
                target: "#insuranceConnector.minimized.unverifiedResult",
                actions: [
                  assign(({ event, context }) => ({
                    lastVerificationResults: event.output.insuranceStates,
                    storefrontPaymentMode: "CASH" as const,
                    isPendingPricesMode: false,
                    primaryInsurance: context.primaryInsurance
                      ? {
                          ...context.primaryInsurance,
                          planName: event.output.insuranceStates[0]?.planName,
                          status: event.output.insuranceStates[0]?.status,
                        }
                      : undefined,
                  })),
                  "syncUnverifiedToStore",
                  enforceSelfPayMode,
                  trackInsuranceUnverifiedFirst,
                ],
              },
            ],
            onError: {
              target: "#insuranceConnector.minimized.unverifiedResult",
              actions: [
                assign(({ event }) => ({
                  error: {
                    code: "VERIFICATION_FAILED",
                    message:
                      (event.error as Error)?.message ||
                      "Unable to verify insurance",
                  },
                  storefrontPaymentMode: "CASH" as const,
                  isPendingPricesMode: false,
                })),
                "syncUnverifiedToStore",
                enforceSelfPayMode,
                trackInsuranceVerificationError,
              ],
            },
          },
          on: {
            CLOSE: {
              target: "#insuranceConnector.closed",
            },
          },
        },

        // ===== SUCCESS CONFIRMATION =====
        successConfirmation: {
          meta: {
            componentName: "Success",
          },
          entry: ["syncAllToStore"],
          after: {
            5000: {
              target: "#insuranceConnector.connected",
            },
          },
        },

        // ===== UNVERIFIED RESULT =====
        unverifiedResult: {
          meta: {
            componentName: "UnverifiedResult",
            variant: "first",
          },
          on: {
            REVIEW_NOW: {
              target: "#insuranceConnector.full.unverifiedRetryPayer",
              actions: assign({
                isRetryFlow: true,
                uiMode: "full",
              }),
            },
            // The user wants to start over with a different insurance.
            // Wipe context AND the saved-to-store insurance from the
            // unverified outcome (synced on entry to this state) so the
            // fresh attempt isn't polluted by the previous payer/memberId
            // or the lingering self-pay flag.
            TRY_ANOTHER: {
              target: "#insuranceConnector.full.insurancePayer",
              actions: [
                "clearInsurance",
                "clearStoreInsurance",
                "clearPendingPricesMode",
                assign({
                  uiMode: "full",
                  isRetryFlow: false,
                }),
              ],
            },
            // The insurance was already saved to the store on the
            // unverified outcome and the storefront was forced into
            // self-pay mode there. Just transition the UI; nothing else
            // to persist here.
            SKIP: {
              target: "#insuranceConnector.connected",
            },
            CLOSE: {
              target: "#insuranceConnector.closed",
            },
          },
        },

        // ===== UNVERIFIED SECOND RESULT =====
        unverifiedSecondResult: {
          meta: {
            componentName: "UnverifiedResult",
            variant: "second",
          },
          on: {
            // Insurance + identity were already synced to the global store
            // when we entered this state from `loadingInsurance`, and the
            // store was forced into self-pay mode there as well. We just
            // need to land the machine in `connected` so the user can pull
            // the insurance card up from the nav menu and disconnect it
            // themselves if they want — without touching the persisted
            // store state again.
            CONTINUE_WITHOUT_INSURANCE: {
              target: "#insuranceConnector.connected",
              actions: [
                assign({
                  storefrontPaymentMode: "CASH",
                  isPendingPricesMode: false,
                }),
                enforceSelfPayMode,
              ],
            },
            PROCEED_WITH_UNVERIFIED: [
              {
                guard: "proceedWithUnverifiedInsuranceEnabled",
                target: "#insuranceConnector.closed",
                // Proceeding with unverified insurance persists three things
                // to the global store:
                //   1. Pending-prices flag (so the storefront renders
                //      "Price pending" and skips up-front payment).
                //   2. Payment mode = INSURED (downstream pricing map +
                //      price toggle derive from this).
                //   3. Insurance + consumer + user identity via
                //      `syncUnverifiedToStore` — the appointment /
                //      checkout surfaces need payer, memberId, state, and
                //      planName to create the visit on the insured path,
                //      even though verification didn't complete.
                // We deliberately skip `setIsInsuranceValidated(true)`
                // (handled separately in `syncAllToStore`) because this
                // connection was not verified.
                actions: [
                  assign({
                    isPendingPricesMode: true,
                    storefrontPaymentMode: "INSURED",
                  }),
                  setPendingPricesMode,
                  "syncStorefrontFlagsToStore",
                  "syncUnverifiedToStore",
                ],
              },
            ],
            CLOSE: {
              target: "#insuranceConnector.closed",
            },
          },
        },

        // ===== INACTIVE RESULT =====
        inactiveResult: {
          meta: {
            componentName: "InactiveResult",
          },
          on: {
            // `TRY_ANOTHER` means the user is editing/replacing the
            // currently-saved insurance. Wipe context and the global store
            // so the new submission doesn't carry over the previous
            // payer/memberId/state or the persisted self-pay flag.
            TRY_ANOTHER: {
              target: "#insuranceConnector.full.insurancePayer",
              actions: [
                "clearInsurance",
                "clearStoreInsurance",
                "clearPendingPricesMode",
                assign({ uiMode: "full", isRetryFlow: false }),
              ],
            },
            // Insurance was already saved to the store and self-pay was
            // already enforced when we entered this state. Land in
            // `connected` so the menu (and the user's option to disconnect)
            // is reachable.
            CONTINUE_WITHOUT_INSURANCE: {
              target: "#insuranceConnector.connected",
              actions: [
                assign({
                  storefrontPaymentMode: "CASH",
                  isPendingPricesMode: false,
                }),
                enforceSelfPayMode,
              ],
            },
          },
        },

        // ===== OUT OF NETWORK RESULT =====
        outOfNetworkResult: {
          meta: {
            componentName: "OutOfNetworkResult",
          },
          on: {
            TRY_ANOTHER: {
              target: "#insuranceConnector.full.insurancePayer",
              actions: [
                "clearInsurance",
                "clearStoreInsurance",
                "clearPendingPricesMode",
                assign({ uiMode: "full", isRetryFlow: false }),
              ],
            },
            CONTINUE_WITHOUT_INSURANCE: {
              target: "#insuranceConnector.connected",
              actions: [
                assign({
                  storefrontPaymentMode: "CASH",
                  isPendingPricesMode: false,
                }),
                enforceSelfPayMode,
              ],
            },
          },
        },

        // ===== ALL PROVIDERS OUT OF NETWORK RESULT =====
        allProvidersOonResult: {
          meta: {
            componentName: "AllProvidersOonResult",
          },
          on: {
            CLOSE: {
              target: "#insuranceConnector.connected.awaitingOonDecision",
            },
            OPEN: {},
            TRY_ANOTHER: {
              target: "#insuranceConnector.full.insurancePayer",
              actions: [
                "clearInsurance",
                "clearStoreInsurance",
                "clearPendingPricesMode",
                assign({
                  uiMode: "full",
                  isRetryFlow: false,
                  providerOonStatus: "idle",
                }),
              ],
            },
            CONTINUE_WITHOUT_INSURANCE: {
              target: "#insuranceConnector.closed",
              actions: [
                "clearInsurance",
                "clearStoreInsurance",
                "clearPendingPricesMode",
                assign({
                  providerOonStatus: "idle",
                }),
              ],
            },
          },
        },

        // ===== INSURANCE MENU (shows connected insurance cards) =====
        insuranceMenu: {
          meta: {
            componentName: "InsuranceMenu",
          },
          on: {
            ADD_SECONDARY: {
              target: "#insuranceConnector.full.secondaryPayerInput",
              actions: assign({ uiMode: "full" }),
            },
            DISCONNECT_PRIMARY: {
              target: "confirmDisconnect",
              actions: assign({ disconnectTarget: "primary" }),
            },
            DISCONNECT_SECONDARY: {
              target: "confirmDisconnect",
              actions: assign({ disconnectTarget: "secondary" }),
            },
            CLOSE: {
              target: "#insuranceConnector.connected",
            },
          },
        },

        // ===== CONFIRM DISCONNECT =====
        confirmDisconnect: {
          meta: {
            componentName: "ConfirmDisconnect",
          },
          on: {
            CANCEL_DISCONNECT: {
              target: "insuranceMenu",
              actions: assign({ disconnectTarget: undefined }),
            },
            CONFIRM_DISCONNECT: [
              {
                guard: "isDisconnectingSecondary",
                target: "insuranceMenu",
                actions: [
                  "clearSecondaryInsurance",
                  "syncInsuranceToStore",
                  assign({ disconnectTarget: undefined }),
                ],
              },
              {
                target: "#insuranceConnector.closed",
                actions: [
                  "clearInsurance",
                  "clearStoreInsurance",
                  "clearPendingPricesMode",
                  assign({ disconnectTarget: undefined }),
                ],
              },
            ],
            CLOSE: {
              target: "#insuranceConnector.connected",
              actions: assign({ disconnectTarget: undefined }),
            },
          },
        },
      },
    },

    // ===== CONNECTED (insurance verified, UI closed, machine stays alive) =====
    connected: {
      initial: "idle",
      entry: [
        assign({
          uiMode: "closed",
          isOpen: false,
        }),
      ],
      on: {
        OPEN: {
          target: "#insuranceConnector.minimized.insuranceMenu",
        },
        PROVIDER_OON_CHECK_STARTED: {
          target: ".checkingProviderOon",
          actions: assign({ providerOonStatus: "checking" }),
        },
        PROVIDER_OON_RESOLVED: [
          {
            guard: "isProviderOonAllOON",
            target: "#insuranceConnector.minimized.allProvidersOonResult",
            actions: assign({ providerOonStatus: "all_oon" }),
          },
          {
            target: ".providerOonResolved",
            actions: assign({ providerOonStatus: "in_network" }),
          },
        ],
        PROVIDER_OON_RESET: {
          target: ".idle",
          actions: assign({ providerOonStatus: "idle" }),
        },
      },
      states: {
        idle: {},
        checkingProviderOon: {},
        providerOonResolved: {},
        awaitingOonDecision: {
          on: {
            OPEN: {
              target: "#insuranceConnector.minimized.allProvidersOonResult",
            },
          },
        },
      },
    },
  },
});
